import { showGrowl } from '../ui/growlState';

export const TENANT_TOKEN_KEY = 'va_tenant_jwt';
export const SUPER_TOKEN_KEY = 'va_super_jwt';

type ApiOk<T> = { success: true; data: T };
type ApiErr = { success: false; error?: { message?: string; code?: string } };

/** Error de respuesta HTTP; usar `status` para decidir p. ej. 401/403 (cerrar sesión) vs 500 (mostrar sin desloguear). */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
    this.code = code;
  }
}

function createApiHttpError(r: Response, bodyJson: unknown): ApiHttpError {
  if (bodyJson && typeof bodyJson === 'object' && 'success' in bodyJson && !(bodyJson as ApiOk<unknown>).success) {
    const e = bodyJson as ApiErr;
    const msg = e.error?.message ?? `HTTP ${r.status}`;
    return new ApiHttpError(msg, r.status, e.error?.code);
  }
  return new ApiHttpError(r.statusText || `HTTP ${r.status}`, r.status);
}

/**
 * Token inválido / caducado / rol incorrecto: limpia storage y va al login correspondiente.
 * No aplica a intentos de login fallidos ni a contraseña obligatoria (403 PASSWORD_CHANGE_REQUIRED).
 */
function redirectIfUnauthorized(path: string, err: ApiHttpError): boolean {
  if (err.status !== 401 && err.status !== 403) return false;
  if (err.code === 'PASSWORD_CHANGE_REQUIRED') return false;
  if (path === '/api/auth/login' || path === '/api/super/login') return false;
  if (path === '/api/auth/change-password') return false;

  if (path.startsWith('/api/super')) {
    localStorage.removeItem(SUPER_TOKEN_KEY);
    window.location.assign('/super/login');
    return true;
  }
  /** 403 en consola no invalida sesión (p. ej. proxies que bloquean ciertos paths); solo 401. */
  if (path.startsWith('/api/console')) {
    if (err.status === 401) {
      localStorage.removeItem(TENANT_TOKEN_KEY);
      window.location.assign('/login');
      return true;
    }
    return false;
  }
  if (path.startsWith('/api/auth/')) {
    localStorage.removeItem(TENANT_TOKEN_KEY);
    window.location.assign('/login');
    return true;
  }
  return false;
}

/** Token inválido o sin permisos: conviene desloguear y volver a login. */
export function isUnauthorizedApiError(e: unknown): e is ApiHttpError {
  return e instanceof ApiHttpError && (e.status === 401 || e.status === 403);
}

/** Opciones para POST / PATCH / DELETE (notificaciones growl). */
export type ApiMutationOpts = {
  timeoutMs?: number;
  /** Mensaje al completar correctamente (toast esquina superior). */
  successMessage?: string;
  /** Mostrar error en growl además de lanzar excepción. Default: true */
  showErrorGrowl?: boolean;
};

function growlError(msg: string, opts?: { showErrorGrowl?: boolean }) {
  if (opts?.showErrorGrowl !== false) {
    showGrowl(msg, 'error');
  }
}

function growlSuccess(msg: string | undefined) {
  if (msg?.trim()) {
    showGrowl(msg.trim(), 'success');
  }
}

export async function apiGet<T>(
  path: string,
  token?: string | null,
  opts?: { showErrorGrowl?: boolean }
): Promise<T> {
  const r = await fetch(path, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  let j: unknown;
  try {
    j = await r.json();
  } catch {
    const err = new ApiHttpError(`HTTP ${r.status}`, r.status);
    redirectIfUnauthorized(path, err);
    throw err;
  }
  if (!r.ok || !('success' in (j as object)) || !(j as ApiOk<T>).success) {
    const err = createApiHttpError(r, j);
    const redirected = redirectIfUnauthorized(path, err);
    if (opts?.showErrorGrowl && !redirected) {
      showGrowl(err.message, 'error');
    }
    throw err;
  }
  return (j as ApiOk<T>).data;
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  token?: string | null,
  opts?: ApiMutationOpts
): Promise<T> {
  const signal =
    opts?.timeoutMs != null && opts.timeoutMs > 0 ? AbortSignal.timeout(opts.timeoutMs) : undefined;
  const r = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
  let j: unknown;
  try {
    j = await r.json();
  } catch {
    const err = new ApiHttpError(`HTTP ${r.status}`, r.status);
    redirectIfUnauthorized(path, err);
    throw err;
  }
  if (!r.ok || !('success' in (j as object)) || !(j as ApiOk<T>).success) {
    const err = createApiHttpError(r, j);
    const redirected = redirectIfUnauthorized(path, err);
    if (opts?.showErrorGrowl !== false && !redirected) {
      growlError(err.message, opts);
    }
    throw err;
  }
  growlSuccess(opts?.successMessage);
  return (j as ApiOk<T>).data;
}

export async function apiDelete<T>(
  path: string,
  token?: string | null,
  opts?: ApiMutationOpts
): Promise<T> {
  const r = await fetch(path, {
    method: 'DELETE',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  let j: unknown;
  try {
    j = await r.json();
  } catch {
    const err = new ApiHttpError(`HTTP ${r.status}`, r.status);
    redirectIfUnauthorized(path, err);
    throw err;
  }
  if (!r.ok || !('success' in (j as object)) || !(j as ApiOk<T>).success) {
    const err = createApiHttpError(r, j);
    const redirected = redirectIfUnauthorized(path, err);
    if (opts?.showErrorGrowl !== false && !redirected) {
      growlError(err.message, opts);
    }
    throw err;
  }
  growlSuccess(opts?.successMessage);
  return (j as ApiOk<T>).data;
}

export async function apiDeleteJson<T>(
  path: string,
  body: unknown,
  token?: string | null,
  opts?: ApiMutationOpts
): Promise<T> {
  const r = await fetch(path, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  let j: unknown;
  try {
    j = await r.json();
  } catch {
    const err = new ApiHttpError(`HTTP ${r.status}`, r.status);
    redirectIfUnauthorized(path, err);
    throw err;
  }
  if (!r.ok || !('success' in (j as object)) || !(j as ApiOk<T>).success) {
    const err = createApiHttpError(r, j);
    const redirected = redirectIfUnauthorized(path, err);
    if (opts?.showErrorGrowl !== false && !redirected) growlError(err.message, opts);
    throw err;
  }
  growlSuccess(opts?.successMessage);
  return (j as ApiOk<T>).data;
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  token?: string | null,
  opts?: ApiMutationOpts
): Promise<T> {
  const r = await fetch(path, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  let j: unknown;
  try {
    j = await r.json();
  } catch {
    const err = new ApiHttpError(`HTTP ${r.status}`, r.status);
    redirectIfUnauthorized(path, err);
    throw err;
  }
  if (!r.ok || !('success' in (j as object)) || !(j as ApiOk<T>).success) {
    const err = createApiHttpError(r, j);
    const redirected = redirectIfUnauthorized(path, err);
    if (opts?.showErrorGrowl !== false && !redirected) {
      growlError(err.message, opts);
    }
    throw err;
  }
  growlSuccess(opts?.successMessage);
  return (j as ApiOk<T>).data;
}

export async function apiPut<T>(
  path: string,
  body: unknown,
  token?: string | null,
  opts?: ApiMutationOpts
): Promise<T> {
  const r = await fetch(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  let j: unknown;
  try {
    j = await r.json();
  } catch {
    const err = new ApiHttpError(`HTTP ${r.status}`, r.status);
    redirectIfUnauthorized(path, err);
    throw err;
  }
  if (!r.ok || !('success' in (j as object)) || !(j as ApiOk<T>).success) {
    const err = createApiHttpError(r, j);
    const redirected = redirectIfUnauthorized(path, err);
    if (opts?.showErrorGrowl !== false && !redirected) {
      growlError(err.message, opts);
    }
    throw err;
  }
  growlSuccess(opts?.successMessage);
  return (j as ApiOk<T>).data;
}
